import type { GhanttTimelineScale } from './types';
import { ghanttSlotsTimeExtent, type GhanttTimelineSlot } from './ganttTimeline';

/**
 * Debe coincidir con {@link ghanttSnapSlotWidthPx} en `GhanttSlotStrip` (rejilla y scroll comparten ancho de celda).
 */
function snapCellPx(slotWidthPx: number): number {
  return Math.max(2, Math.round(slotWidthPx));
}

function slotEndExclusiveMs(
  slots: readonly GhanttTimelineSlot[],
  scale: GhanttTimelineScale,
  index: number
): number {
  if (index + 1 < slots.length) {
    return slots[index + 1]!.startMs;
  }
  const ext = ghanttSlotsTimeExtent(slots, scale);
  return ext?.endExclusiveMs ?? slots[index]!.startMs;
}

function findSlotIndexAtOrBefore(slots: readonly GhanttTimelineSlot[], anchorMs: number): number {
  if (slots.length === 0) return 0;
  if (anchorMs < slots[0]!.startMs) {
    return 0;
  }
  let lo = 0;
  let hi = slots.length - 1;
  let ans = hi;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (slots[mid]!.startMs <= anchorMs) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}

/**
 * Posición horizontal en px sobre el contenido scrollable (origen = inicio del primer slot),
 * alineada a la misma rejilla que `GhanttSlotStrip` / cabecera.
 */
export function ghanttContentPixelToAnchorMs(
  slots: readonly GhanttTimelineSlot[],
  scale: GhanttTimelineScale,
  slotWidthPx: number,
  contentPixel: number
): number | null {
  const cell = snapCellPx(slotWidthPx);
  if (slots.length === 0 || cell <= 0) return null;
  const totalPx = slots.length * cell;
  const ext = ghanttSlotsTimeExtent(slots, scale);
  /** Evita `floor(slotFloat) === slots.length` cuando el pixel cae en el borde derecho del contenido. */
  const p = Math.max(0, Math.min(contentPixel, Math.max(0, totalPx - 1e-3)));
  const slotFloat = p / cell;
  const i = Math.min(slots.length - 1, Math.max(0, Math.floor(slotFloat)));
  const u = slotFloat - i;
  const s0 = slots[i]!.startMs;
  const s1 = slotEndExclusiveMs(slots, scale, i);
  const dur = Math.max(1, s1 - s0);
  const clampedU = Math.max(0, Math.min(1, u));
  let ms = s0 + clampedU * dur;
  if (ext && ms >= ext.endExclusiveMs) {
    ms = ext.endExclusiveMs - 1;
  }
  return ms;
}

export function ghanttAnchorMsToContentPixel(
  slots: readonly GhanttTimelineSlot[],
  scale: GhanttTimelineScale,
  slotWidthPx: number,
  anchorMs: number
): number | null {
  const cell = snapCellPx(slotWidthPx);
  if (slots.length === 0 || cell <= 0) return null;
  const ext = ghanttSlotsTimeExtent(slots, scale);
  if (ext && anchorMs <= ext.startMs) {
    return 0;
  }
  if (ext && anchorMs >= ext.endExclusiveMs) {
    return slots.length * cell;
  }
  const i = findSlotIndexAtOrBefore(slots, anchorMs);
  const s0 = slots[i]!.startMs;
  const s1 = slotEndExclusiveMs(slots, scale, i);
  const dur = Math.max(1, s1 - s0);
  const u = Math.max(0, Math.min(1, (anchorMs - s0) / dur));
  return (i + u) * cell;
}
